import {
  createContext,
  startTransition,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Dialog } from './Dialog';

/**
 * Megerosites visszafordithatatlan muveletek elott.
 *
 * ─── Miert kell, es miert IGY? ──────────────────────────────────────────────
 *
 * A torles nem vonhato vissza, a kattintas viszont egy pillanat: egy rossz
 * helyre csuszott egermozdulat egy kozos csatorna canvasat vitte volna el,
 * kerdes nelkul. Egy "biztos?" nem sokat ker, de pontosan azt a pillanatot
 * adja vissza, ami hianyzott.
 *
 * A `window.confirm` helyett sajat parbeszed, harom okbol: a bongeszo sajat
 * ablaka BLOKKOLJA az egesz lapot (az ablak alatt tovabb erkezo uzenetek
 * megallnanak), nem lehet magyarul es a felulet hangjan megfogalmazni, es a
 * beagyazott asztali alkalmazasban maskepp nez ki, mint minden mas.
 *
 * Igerettel dolgozik, hogy a hivo oldalon egyetlen sor legyen:
 *
 *     if (!(await confirm({ title: 'Canvas törlése', ... }))) return;
 */
export interface ConfirmOptions {
  title: string;
  /** Mi tortenik, es mi nem vonhato vissza. Egy-ket mondat. */
  body?: ReactNode;
  /** A megerosito gomb felirata - IGE, nem "OK": "Törlés", "Kivétel". */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Piros gomb: torles es tarsai. Alapertelmezes szerint igen. */
  danger?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn>(async () => true);

/** A megerosito parbeszed hivasa. Igazat ad, ha a felhasznalo ravagta. */
export function useConfirm(): ConfirmFn {
  return useContext(ConfirmContext);
}

interface PendingConfirm extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirm = useCallback<ConfirmFn>(
    (options) =>
      new Promise<boolean>((resolve) => {
        setPending({ ...options, resolve });
      }),
    [],
  );

  /*
    A BEZARAS is valasz - nemleges.

    Escape-re vagy a hatterre kattintva a parbeszed eltunik; ha ilyenkor nem
    oldanank fel az igeretet, a hivo orokre varna, es a muvelet csendben
    beragadna.
  */
  const close = useCallback((answer: boolean) => {
    // Atmenetkent: a megerosito ablak is visszahuzodik, mint a tobbi (lasd Dialog).
    startTransition(() =>
      setPending((current) => {
        current?.resolve(answer);
        return null;
      }),
    );
  }, []);

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}

      <Dialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) close(false);
        }}
        title={pending?.title ?? ''}
      >
        {pending?.body && <div className="text-sm text-ink-muted">{pending.body}</div>}

        {/*
          A FOKUSZ veszelyes muveletnel a Mégse gombon all.

          Kulonben egy veletlen Enter/Szokoz - amit az ember epp a gombnyomas
          utan meg a billentyuzeten tart - kerdes nelkul torolne. Nem veszelyes
          megerositesnel (danger: false) maradhat a megerosito gombon.
        */}
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            autoFocus={pending?.danger !== false}
            onClick={() => close(false)}
            className="rounded-control px-3 py-2 text-sm text-ink-muted transition-colors duration-(--duration-quick) hover:bg-surface-hover hover:text-ink"
          >
            {pending?.cancelLabel ?? 'Mégse'}
          </button>

          <button
            type="button"
            autoFocus={pending?.danger === false}
            onClick={() => close(true)}
            className={[
              'rounded-control px-4 py-2 text-sm font-semibold transition-colors duration-(--duration-quick)',
              pending?.danger === false
                ? 'bg-accent text-accent-ink hover:bg-accent-hover'
                : 'bg-[var(--danger)] text-white hover:brightness-110',
            ].join(' ')}
          >
            {pending?.confirmLabel ?? 'Törlés'}
          </button>
        </div>
      </Dialog>
    </ConfirmContext.Provider>
  );
}
